import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { ArrowLeft, Mail, Calendar, CheckCircle, XCircle, Eye, X } from 'lucide-react';
import { adminAuth } from '../services/adminAuth';

interface EmailCampaignLead {
  id: string;
  customer_email: string;
  customer_first_name: string | null;
  customer_last_name: string | null;
  selected_frame: string | null;
  selected_style: string | null;
  frame_image_url: string | null;
  upscaled_image_url: string | null;
  watercolor_image_url: string | null;
  pencil_image_url: string | null;
  oil_image_url: string | null;
  order_status: string | null;
  stripe_checkout_session_id: string | null;
  created_at: string;
  checkout_initiated_at: string | null;
  property_token: string | null;
}

const EmailCampaign: React.FC = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState<EmailCampaignLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewLead, setPreviewLead] = useState<EmailCampaignLead | null>(null);
  const [showTestEmailModal, setShowTestEmailModal] = useState(false);
  const [testEmailLead, setTestEmailLead] = useState<EmailCampaignLead | null>(null);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [testEmailSuccess, setTestEmailSuccess] = useState<string | null>(null);
  const [testEmailError, setTestEmailError] = useState<string | null>(null);
  
  // Pagination and selection
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedLeads, setSelectedLeads] = useState<Set<string>>(new Set());
  const [sendingBulk, setSendingBulk] = useState(false);
  const [filter, setFilter] = useState<'all' | 'unsent' | 'sent'>('unsent');
  const [sentEmails, setSentEmails] = useState<Set<string>>(new Set());
  const [clickedEmails, setClickedEmails] = useState<Set<string>>(new Set());
  const leadsPerPage = 20;

  useEffect(() => {
    // Check authentication on component mount
    if (!adminAuth.isAuthenticated()) {
      navigate('/admin-login');
      return;
    }
    fetchLeads();
  }, [navigate]);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch sent emails, clicks, and unsubscribes
      const { data: sentData } = await supabase
        .from('email_campaign_sends')
        .select('property_search_id, email, campaign_name');
      
      const sentSet = new Set(
        sentData?.filter(s => s.campaign_name !== 'unsubscribed' && !s.campaign_name.includes('-clicked')).map(s => s.property_search_id) || []
      );
      setSentEmails(sentSet);
      
      const clickedSet = new Set(
        sentData?.filter(s => s.campaign_name.includes('-clicked')).map(s => s.property_search_id) || []
      );
      setClickedEmails(clickedSet);

      // Get all property_searches with email addresses
      // Include people with no order status OR status that's not completed/processing
      const { data, error: fetchError } = await supabase
        .from('property_searches')
        .select(`
          id,
          customer_email,
          customer_first_name,
          customer_last_name,
          selected_frame,
          selected_style,
          frame_image_url,
          upscaled_image_url,
          watercolor_image_url,
          pencil_image_url,
          oil_image_url,
          order_status,
          stripe_checkout_session_id,
          created_at,
          checkout_initiated_at,
          property_token
        `)
        .not('customer_email', 'is', null)
        .neq('customer_email', '')
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      // Filter out completed/processing orders AND test users
      const filteredData = (data || []).filter(lead => {
        // Filter out test users by name (Thomas Kleist and Simon Berg)
        const fullName = `${lead.customer_first_name || ''} ${lead.customer_last_name || ''}`.toLowerCase().trim();
        if (fullName === 'thomas kleist' || fullName === 'simon berg') {
          return false;
        }
        
        // Filter out test users by email
        const email = lead.customer_email.toLowerCase().trim();
        if (email === 'thomkleist@gmail.com') {
          return false;
        }
        
        // Include if order_status is null/undefined OR if it's not completed/processing
        return !lead.order_status || (lead.order_status !== 'completed' && lead.order_status !== 'processing');
      });

      // Deduplicate by email - keep only the most recent entry per email
      const emailMap = new Map<string, EmailCampaignLead>();
      filteredData.forEach(lead => {
        const email = lead.customer_email.toLowerCase().trim();
        const existing = emailMap.get(email);
        
        // Keep the entry with the most recent created_at (data is already sorted by created_at desc)
        if (!existing || new Date(lead.created_at) > new Date(existing.created_at)) {
          emailMap.set(email, lead);
        }
      });

      const deduplicatedData = Array.from(emailMap.values());

      // Filter out unsubscribed emails
      const unsubscribedEmails = new Set(
        sentData?.filter(s => s.campaign_name === 'unsubscribed').map(s => s.email.toLowerCase()) || []
      );
      
      console.log('Unsubscribed emails:', Array.from(unsubscribedEmails));
      console.log('Clicked emails:', Array.from(clickedSet));
      console.log('Sent emails:', Array.from(sentSet));
      console.log('Total leads before filtering:', deduplicatedData.length);
      
      const filteredForUnsubscribes = deduplicatedData.filter(
        lead => !unsubscribedEmails.has(lead.customer_email.toLowerCase())
      );
      
      console.log('Leads after filtering unsubscribes:', filteredForUnsubscribes.length);

      setLeads(filteredForUnsubscribes);
    } catch (err) {
      console.error('Error fetching leads:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFrameUrl = (frameId: string | null) => {
    if (!frameId) return null;
    
    const frameMap: Record<string, string> = {
      'gold': 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/Templates//Gold_M.png',
      'mocca': 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/Templates//Mocca_M.png',
      'natural': 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/Templates//Natural_M.png',
      'white': 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/Templates//White_M.png',
      'black': 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/Templates//Black_M.png'
    };
    
    return frameMap[frameId] || null;
  };

  const getFrameDisplayName = (frameId: string | null) => {
    if (!frameId) return 'No frame';
    
    const frameNames: Record<string, string> = {
      'gold': 'Gold',
      'mocca': 'Espresso',
      'natural': 'Natural',
      'white': 'White',
      'black': 'Black'
    };
    
    return frameNames[frameId] || frameId;
  };

  const getStyleDisplayName = (styleId: string | null) => {
    if (!styleId) return 'No style';
    
    const styleNames: Record<string, string> = {
      'pencil': 'Pencil',
      'watercolor': 'Watercolor',
      'oil': 'Oil Painting',
      'charcoal': 'Charcoal',
      'chalk': 'Chalk'
    };
    
    return styleNames[styleId] || styleId;
  };

  const getDisplayImage = (lead: EmailCampaignLead): string | null => {
    // Priority 1: If they have both selected_frame AND selected_style, use upscaled_image_url
    if (lead.selected_frame && lead.selected_style && lead.upscaled_image_url) {
      return lead.upscaled_image_url;
    }
    
    // Priority 2: Use frame_image_url if available
    if (lead.frame_image_url) {
      return lead.frame_image_url;
    }
    
    // Priority 3: If they have selected_style, use the corresponding image
    if (lead.selected_style) {
      if (lead.selected_style === 'watercolor' && lead.watercolor_image_url) {
        return lead.watercolor_image_url;
      }
      if (lead.selected_style === 'pencil' && lead.pencil_image_url) {
        return lead.pencil_image_url;
      }
      if (lead.selected_style === 'oil' && lead.oil_image_url) {
        return lead.oil_image_url;
      }
    }
    
    // Priority 4: Fallback to watercolor if available
    if (lead.watercolor_image_url) {
      return lead.watercolor_image_url;
    }
    
    // Priority 5: Then try pencil
    if (lead.pencil_image_url) {
      return lead.pencil_image_url;
    }
    
    // Priority 6: Finally try oil
    if (lead.oil_image_url) {
      return lead.oil_image_url;
    }
    
    return null;
  };

  const getDisplayFrame = (lead: EmailCampaignLead): string | null => {
    // Use selected frame if available, otherwise default to black
    return lead.selected_frame || 'black';
  };

  const getDisplayStyle = (lead: EmailCampaignLead): string => {
    // If they have a selected style, use it
    if (lead.selected_style) {
      return lead.selected_style;
    }
    
    // Otherwise, figure out which image we're showing and use that style
    if (lead.frame_image_url) {
      return lead.selected_style || 'watercolor';
    }
    
    if (lead.watercolor_image_url) {
      return 'watercolor';
    }
    
    if (lead.pencil_image_url) {
      return 'pencil';
    }
    
    if (lead.oil_image_url) {
      return 'oil';
    }
    
    return 'watercolor';
  };

  const generateEmailTemplate = (lead: EmailCampaignLead): string => {
    const imageUrl = getDisplayImage(lead);
    const frameUrl = getFrameUrl(getDisplayFrame(lead));
    const customerName = lead.customer_first_name || 'there';
    const frameName = getFrameDisplayName(getDisplayFrame(lead));
    const styleName = getStyleDisplayName(getDisplayStyle(lead));
    
    // Add tracking parameters to the link
    const orderLink = lead.property_token 
      ? `https://framemyhome.ai/${lead.property_token}?utm_source=email-campaign&utm_medium=email&utm_campaign=holiday-2024`
      : 'https://framemyhome.ai?utm_source=email-campaign&utm_medium=email&utm_campaign=holiday-2024';

    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Complete Your Custom Frame</title>
</head>
<body style="margin: 0; padding: 0; font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; background-color: #f5f1e8;">
  <table role="presentation" style="width: 100%; border-collapse: collapse; background-color: #f5f1e8;">
    <tr>
      <td align="center" style="padding: 40px 20px;">
        <table role="presentation" style="max-width: 600px; width: 100%; background-color: #ffffff;">
          
          <!-- Logo Header -->
          <tr>
            <td style="padding: 40px 40px 30px; text-align: center; background-color: #ffffff;">
              <img src="https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/website_assets//Framemyhome.png" alt="FrameMyHome" style="width: 160px; height: auto;">
            </td>
          </tr>

          <!-- Divider -->
          <tr>
            <td style="padding: 0 40px;">
              <div style="border-top: 1px solid #e0d4c3; margin: 0;"></div>
            </td>
          </tr>

          <!-- Main Content -->
          <tr>
            <td style="padding: 40px 40px 30px; background-color: #ffffff;">
              <p style="margin: 0 0 24px; color: #2c2c2c; font-size: 18px; line-height: 1.6; font-weight: 600; text-align: center;">
                🎁 Order now in time for the holidays. 🎁
              </p>
              
              <p style="margin: 0 0 24px; color: #555; font-size: 16px; line-height: 1.6;">
                We noticed you created this beautiful artwork with us. If you are still looking for a unique gift, order by <strong>December 10</strong> with <strong>20% off</strong> and your custom frame will arrive before the holidays. 🎄
              </p>

              ${imageUrl && frameUrl ? `
              <!-- Frame Preview with Background Image -->
              <table role="presentation" style="width: 100%; margin: 30px 0 40px;">
                <tr>
                  <td align="center" style="padding: 20px 0;">
                    <table role="presentation" style="width: 374px; max-width: 100%; margin: 0 auto; background-image: url('${imageUrl}'); background-size: 60% auto; background-position: center; background-repeat: no-repeat;">
                      <tr>
                        <td style="padding: 0;">
                          <img src="${frameUrl}" alt="${frameName} frame" style="width: 100%; height: auto; display: block; border-radius: 8px;">
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>
              ` : imageUrl ? `
              <!-- Artwork Only (No Frame) -->
              <table role="presentation" style="width: 100%; margin: 30px 0 40px;">
                <tr>
                  <td align="center" style="padding: 20px 0;">
                    <img src="${imageUrl}" alt="Your custom artwork" style="max-width: 300px; width: 100%; height: auto; display: block; border-radius: 8px;">
                  </td>
                </tr>
              </table>
              ` : ''}

              <p style="margin: 0 0 24px; color: #555; font-size: 16px; line-height: 1.6;">
                FrameMyHome transforms your cherished memories into premium artwork, delivered ready to hang. Whether it's a beloved home or a special pet, we create premium-quality framed prints that make the perfect holiday gift. 🏡 🐾
              </p>

              <table role="presentation" style="width: 100%; margin: 20px 0 30px; background-color: #ffffff; border-radius: 8px; border: 1px solid #e0d4c3;">
                <tr>
                  <td style="padding: 20px 24px;">
                    <p style="margin: 0 0 12px; color: #555; font-size: 15px; line-height: 1.6;">
                      🎨 Premium artwork delivered ready to hang
                    </p>
                    <p style="margin: 0 0 12px; color: #555; font-size: 15px; line-height: 1.6;">
                      🎁 Perfect gift for homes and pets
                    </p>
                    <p style="margin: 0; color: #555; font-size: 15px; line-height: 1.6;">
                      ✨ A gift that brings joy for years
                    </p>
                  </td>
                </tr>
              </table>

              <!-- CTA Button -->
              <table role="presentation" style="width: 100%; margin: 35px 0 15px;">
                <tr>
                  <td align="center">
                    <a href="${orderLink}" style="display: inline-block; padding: 16px 48px; background-color: #2c2c2c; color: #ffffff; text-decoration: none; font-size: 16px; font-weight: 600; border-radius: 8px; border: 2px solid #2c2c2c;">
                      Complete Your Order 🎄
                    </a>
                  </td>
                </tr>
              </table>

              <!-- Secondary CTA Button -->
              <table role="presentation" style="width: 100%; margin: 0 0 30px;">
                <tr>
                  <td align="center">
                    <a href="https://framemyhome.ai" style="display: inline-block; padding: 16px 48px; background-color: transparent; color: #2c2c2c; text-decoration: none; font-size: 16px; font-weight: 600; border-radius: 8px; border: 2px solid #2c2c2c;">
                      Create New Artwork ✨
                    </a>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Divider -->
          <tr>
            <td style="padding: 0 40px;">
              <div style="border-top: 1px solid #e0d4c3; margin: 0;"></div>
            </td>
          </tr>

          <!-- Footer -->
          <tr>
            <td style="padding: 30px 40px; background-color: #2c2c2c; text-align: center;">
              <p style="margin: 0 0 5px; color: #ffffff; font-size: 14px; font-weight: 600; opacity: 0.8;">
                Framemyhome
              </p>
              <p style="margin: 0 0 15px; color: #ffffff; font-size: 14px; opacity: 0.8;">
                Transform your memories into beautiful AI art
              </p>
              <p style="margin: 0 0 20px; color: #F5ECE1; font-size: 14px;">
                <a href="mailto:support@framemyhome.ai" style="color: #F5ECE1; text-decoration: none;">support@framemyhome.ai</a>
              </p>
              <p style="margin: 0 0 10px; color: #ffffff; font-size: 12px; opacity: 0.8;">
                © 2025 Framemyhome. All rights reserved.
              </p>
              <p style="margin: 0; color: #F5ECE1; font-size: 12px; opacity: 0.9;">
                <a href="https://framemyhome.ai/unsubscribe?email=${encodeURIComponent(lead.customer_email)}" style="color: #F5ECE1; text-decoration: underline; cursor: pointer;">Unsubscribe</a>
              </p>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>
    `.trim();
  };

  const sendTestEmail = async () => {
    if (!testEmail || !testEmail.includes('@')) {
      setTestEmailError('Please enter a valid email address');
      return;
    }

    // Use the testEmailLead if available, otherwise use the first lead or create a sample lead
    const sampleLead = testEmailLead || leads[0] || {
      id: 'sample',
      customer_email: testEmail,
      customer_first_name: 'Test',
      customer_last_name: 'User',
      selected_frame: 'black',
      selected_style: 'watercolor',
      frame_image_url: null,
      upscaled_image_url: null,
      watercolor_image_url: 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/propertyimages/sample.jpg',
      pencil_image_url: null,
      oil_image_url: null,
      order_status: null,
      stripe_checkout_session_id: null,
      created_at: new Date().toISOString(),
      checkout_initiated_at: null,
      property_token: 'SAMPLETOK123'
    };

    try {
      setSendingTestEmail(true);
      setTestEmailError(null);
      setTestEmailSuccess(null);

      const emailHtml = generateEmailTemplate(sampleLead);

      const payload = {
        to: testEmail,
        subject: 'Last chance: Order your premium Artwork for the holiday',
        html: emailHtml,
        from: 'FrameMyHome <noreply@framemyhome.ai>',
        replyTo: 'support@framemyhome.ai'
      };

      console.log('Sending test email to:', testEmail);
      console.log('Payload:', {
        to: payload.to,
        subject: payload.subject,
        from: payload.from,
        htmlLength: payload.html.length
      });

      // Use direct fetch instead of supabase.functions.invoke for better compatibility
      const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL || '').trim().replace(/[^\x00-\x7F]/g, '');
      const supabaseKey = (import.meta.env.VITE_SUPABASE_ANON_KEY || '').trim().replace(/[^\x00-\x7F]/g, '');
      
      console.log('URL valid:', /^https?:\/\//.test(supabaseUrl));
      console.log('Key length:', supabaseKey.length);
      
      if (!supabaseUrl || !supabaseKey) {
        throw new Error('Missing Supabase configuration');
      }
      
      const response = await fetch(
        `${supabaseUrl}/functions/v1/send-resend-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${supabaseKey}`
          },
          body: JSON.stringify(payload)
        }
      );

      console.log('Response status:', response.status);
      const result = await response.json();
      console.log('Response result:', result);

      if (!response.ok) {
        console.error('Response error:', result);
        throw new Error(result.error || `Server error: ${response.status}`);
      }

      if (!result.success) {
        console.error('Result error:', result);
        throw new Error(result.error || 'Failed to send test email');
      }

      setTestEmailSuccess(`Test email sent successfully to ${testEmail}!`);
      setTimeout(() => {
        setShowTestEmailModal(false);
        setTestEmail('');
        setTestEmailSuccess(null);
        setTestEmailLead(null);
      }, 2000);
    } catch (err) {
      console.error('Error sending test email:', err);
      setTestEmailError(err instanceof Error ? err.message : 'Failed to send test email');
    } finally {
      setSendingTestEmail(false);
    }
  };

  // Add test entry
  const addTestEntry = () => {
    const lastLead = leads[0];
    if (!lastLead) {
      alert('No leads available to duplicate');
      return;
    }

    // Generate a proper UUID for the test entry
    const testUuid = crypto.randomUUID();

    const testLead: EmailCampaignLead = {
      ...lastLead,
      id: testUuid,
      customer_email: 'thomas@mshstudio.ai',
      customer_first_name: 'Thomas',
      customer_last_name: 'Test',
      property_token: lastLead.property_token || 'TESTTOK123',
      created_at: new Date().toISOString()
    };

    setLeads([testLead, ...leads]);
  };

  // Filter logic
  const filteredLeads = leads.filter(lead => {
    if (filter === 'all') return true;
    if (filter === 'sent') return sentEmails.has(lead.id);
    if (filter === 'unsent') return !sentEmails.has(lead.id);
    return true;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredLeads.length / leadsPerPage);
  const paginatedLeads = filteredLeads.slice(
    (currentPage - 1) * leadsPerPage,
    currentPage * leadsPerPage
  );

  // Selection handlers
  const toggleSelectAll = () => {
    if (selectedLeads.size === paginatedLeads.length) {
      setSelectedLeads(new Set());
    } else {
      setSelectedLeads(new Set(paginatedLeads.map(lead => lead.id)));
    }
  };

  const toggleSelectLead = (leadId: string) => {
    const newSelected = new Set(selectedLeads);
    if (newSelected.has(leadId)) {
      newSelected.delete(leadId);
    } else {
      newSelected.add(leadId);
    }
    setSelectedLeads(newSelected);
  };

  // Bulk send emails
  const sendBulkEmails = async () => {
    if (selectedLeads.size === 0) {
      alert('Please select at least one lead to send emails to');
      return;
    }

    if (!confirm(`Send emails to ${selectedLeads.size} selected leads?`)) {
      return;
    }

    setSendingBulk(true);
    let successCount = 0;
    let failCount = 0;
    let skippedCount = 0;

    // Check for unsubscribed emails before sending
    const { data: unsubscribeData } = await supabase
      .from('email_campaign_sends')
      .select('email')
      .eq('campaign_name', 'unsubscribed');
    
    console.log('Unsubscribe data from DB:', unsubscribeData);
    
    const unsubscribedEmails = new Set(
      unsubscribeData?.map(u => u.email.toLowerCase()) || []
    );
    
    console.log('Unsubscribed emails set:', Array.from(unsubscribedEmails));

    for (const leadId of selectedLeads) {
      const lead = leads.find(l => l.id === leadId);
      if (!lead) continue;

      // Skip if unsubscribed
      if (unsubscribedEmails.has(lead.customer_email.toLowerCase())) {
        console.log(`Skipping unsubscribed email: ${lead.customer_email}`);
        skippedCount++;
        continue;
      }

      try {
        const emailHtml = generateEmailTemplate(lead);
        const payload = {
          to: lead.customer_email,
          subject: 'Last chance: Order your premium Artwork for the holiday',
          html: emailHtml,
          from: 'FrameMyHome <noreply@framemyhome.ai>',
          replyTo: 'support@framemyhome.ai'
        };

        const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL || '').trim().replace(/[^\x00-\x7F]/g, '');
        const supabaseKey = (import.meta.env.VITE_SUPABASE_ANON_KEY || '').trim().replace(/[^\x00-\x7F]/g, '');

        const response = await fetch(
          `${supabaseUrl}/functions/v1/send-resend-email`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${supabaseKey}`
            },
            body: JSON.stringify(payload)
          }
        );

        if (response.ok) {
          successCount++;
          
          // Record the send in the tracking table
          try {
            // Check if this is a real property_search record or a test entry
            // Test entries or entries without valid property_search_id should use null
            const { data: propertyExists } = await supabase
              .from('property_searches')
              .select('id')
              .eq('id', lead.id)
              .maybeSingle();

            await supabase.from('email_campaign_sends').insert({
              property_search_id: propertyExists ? lead.id : null,
              email: lead.customer_email,
              campaign_name: 'holiday-2024'
            });
            
            // Update the UI state
            setSentEmails(prev => new Set([...prev, lead.id]));
          } catch (trackError) {
            console.error('Failed to track send:', trackError);
          }
        } else {
          failCount++;
        }

        // Small delay between sends
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (error) {
        console.error(`Failed to send to ${lead.customer_email}:`, error);
        failCount++;
      }
    }

    setSendingBulk(false);
    setSelectedLeads(new Set());
    
    let message = `Sent ${successCount} emails successfully.`;
    if (failCount > 0) message += ` ${failCount} failed.`;
    if (skippedCount > 0) message += ` ${skippedCount} skipped (unsubscribed).`;
    
    alert(message);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading email campaign leads...</div>
      </div>
    );
  }

  if (!adminAuth.isAuthenticated()) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link
              to="/admin"
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
              <Mail className="w-6 h-6 text-indigo-600" />
              Email Campaign Leads
            </h1>
          </div>
          <div className="flex items-center gap-4">
            {/* Filter Dropdown */}
            <select
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value as 'all' | 'unsent' | 'sent');
                setCurrentPage(1);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="unsent">Unsent Only</option>
              <option value="all">All Leads</option>
              <option value="sent">Sent Only</option>
            </select>

            {selectedLeads.size > 0 && (
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-indigo-600">
                  {selectedLeads.size} selected
                </span>
                <button
                  onClick={sendBulkEmails}
                  disabled={sendingBulk}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sendingBulk ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4" />
                      Send to Selected
                    </>
                  )}
                </button>
              </div>
            )}
            <div className="text-sm text-gray-500">
              {filteredLeads.length} of {leads.length} {leads.length === 1 ? 'lead' : 'leads'}
            </div>
            <button
              onClick={addTestEntry}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
            >
              <Mail className="w-4 h-4" />
              Add Test Entry
            </button>
            <button
              onClick={() => {
                setTestEmailLead(null);
                setShowTestEmailModal(true);
              }}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
            >
              <Mail className="w-4 h-4" />
              Send Test Email
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6">
            {error}
          </div>
        )}

        {leads.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <Mail className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No leads found</h3>
            <p className="text-gray-500">
              No email addresses found that aren't in completed or processing status.
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedLeads.size === paginatedLeads.length && paginatedLeads.length > 0}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Frame Preview
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Order Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Property Token
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Checkout ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Visited
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedLeads.map((lead) => (
                  <tr key={lead.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedLeads.has(lead.id)}
                        onChange={() => toggleSelectLead(lead.id)}
                        className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="relative w-24 h-24">
                        {getDisplayImage(lead) ? (
                          <>
                            {/* Artwork image */}
                            <img
                              src={getDisplayImage(lead)!}
                              alt={getStyleDisplayName(getDisplayStyle(lead))}
                              className="absolute inset-0 w-full h-full object-cover rounded-lg shadow-sm"
                            />
                            {/* Frame overlay */}
                            {getFrameUrl(getDisplayFrame(lead)) && (
                              <img
                                src={getFrameUrl(getDisplayFrame(lead))!}
                                alt={getFrameDisplayName(getDisplayFrame(lead))}
                                className="absolute inset-0 w-full h-full object-cover rounded-lg"
                              />
                            )}
                            {/* Labels */}
                            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded-b-lg">
                              <div className="font-medium">{getFrameDisplayName(getDisplayFrame(lead))}</div>
                              <div className="text-gray-300">{getStyleDisplayName(getDisplayStyle(lead))}</div>
                            </div>
                          </>
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded-lg text-gray-400 text-xs">
                            No image
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        {lead.customer_first_name || lead.customer_last_name ? (
                          <div className="text-sm font-medium text-gray-900">
                            {lead.customer_first_name} {lead.customer_last_name}
                          </div>
                        ) : (
                          <div className="text-sm text-gray-400 italic">No name</div>
                        )}
                        <div className="flex items-center gap-2">
                          <div className="text-sm text-gray-600">{lead.customer_email}</div>
                          {sentEmails.has(lead.id) && (
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded">
                              Sent
                            </span>
                          )}
                          {clickedEmails.has(lead.id) && (
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                              Clicked ✅
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {lead.order_status ? (
                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          lead.order_status === 'completed' 
                            ? 'bg-green-100 text-green-800'
                            : lead.order_status === 'processing'
                            ? 'bg-blue-100 text-blue-800'
                            : lead.order_status === 'failed'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {lead.order_status === 'completed' && <CheckCircle className="w-3 h-3" />}
                          {lead.order_status === 'failed' && <XCircle className="w-3 h-3" />}
                          {lead.order_status}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-sm italic">No order</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {lead.property_token ? (
                        <div className="text-xs text-gray-700 font-mono">
                          {lead.property_token}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {lead.stripe_checkout_session_id ? (
                        <div className="text-xs text-gray-500 font-mono truncate max-w-xs">
                          {lead.stripe_checkout_session_id.substring(0, 20)}...
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        {formatDate(lead.created_at)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => setPreviewLead(lead)}
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-md hover:bg-indigo-100 transition-colors text-sm font-medium"
                      >
                        <Eye className="w-4 h-4" />
                        Preview
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing <span className="font-medium">{((currentPage - 1) * leadsPerPage) + 1}</span> to{' '}
                  <span className="font-medium">{Math.min(currentPage * leadsPerPage, leads.length)}</span> of{' '}
                  <span className="font-medium">{leads.length}</span> results
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1 border rounded-md text-sm font-medium ${
                        currentPage === page
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Email Preview Modal */}
      {previewLead && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Email Preview</h3>
                <p className="text-sm text-gray-500 mt-1">
                  To: {previewLead.customer_email}
                </p>
              </div>
              <button
                onClick={() => setPreviewLead(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content - Email Preview */}
            <div className="flex-1 overflow-auto p-6 bg-gray-50">
              <div className="bg-white rounded-lg shadow-sm">
                <iframe
                  srcDoc={generateEmailTemplate(previewLead)}
                  className="w-full h-[600px] border-0 rounded-lg"
                  title="Email Preview"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setPreviewLead(null)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setTestEmailLead(previewLead);
                  setShowTestEmailModal(true);
                }}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium flex items-center gap-2"
              >
                <Mail className="w-4 h-4" />
                Send Test Email
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Test Email Modal */}
      {showTestEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <Mail className="w-5 h-5 text-indigo-600" />
                Send Test Email
              </h2>
              <button
                onClick={() => {
                  setShowTestEmailModal(false);
                  setTestEmail('');
                  setTestEmailError(null);
                  setTestEmailSuccess(null);
                  setTestEmailLead(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-4">
              <p className="text-sm text-gray-600 mb-4">
                {testEmailLead 
                  ? `Send a test email with ${testEmailLead.customer_first_name || 'this customer'}'s artwork and data.`
                  : 'Send a test email using sample data to verify the template looks correct.'
                }
              </p>
              
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !sendingTestEmail) {
                    sendTestEmail();
                  }
                }}
                placeholder="your@email.com"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                disabled={sendingTestEmail}
              />

              {testEmailError && (
                <div className="mt-3 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
                  {testEmailError}
                </div>
              )}

              {testEmailSuccess && (
                <div className="mt-3 text-sm text-green-600 bg-green-50 px-3 py-2 rounded-lg flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  {testEmailSuccess}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  setShowTestEmailModal(false);
                  setTestEmail('');
                  setTestEmailError(null);
                  setTestEmailSuccess(null);
                  setTestEmailLead(null);
                }}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={sendingTestEmail}
              >
                Cancel
              </button>
              <button
                onClick={sendTestEmail}
                disabled={sendingTestEmail || !testEmail}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {sendingTestEmail ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4" />
                    Send Test
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailCampaign;

